import api from './api';

export type ImportEntity = 'products' | 'customers' | 'suppliers';
export type ImportDuplicateAction = 'skip' | 'upsert' | 'create';

export type ImportPreviewCell = string | number | boolean | null;
export type ImportPreviewRow = ImportPreviewCell[];

export interface ImportUploadResponse {
  upload_id: string;
  columns: string[];
  preview: ImportPreviewRow[];
  total_rows: number;
  file_name: string;
  file_size: number;
  parser_type: string;
}

export interface ImportMappingTemplate {
  template_id: string;
  tenant_id: string | null;
  name: string;
  source_system: string;
  entity: ImportEntity;
  is_built_in: boolean;
  mapping: Record<string, string>;
  created_by: string | null;
  created_at?: string;
  updated_at?: string;
}

export interface CreateImportTemplatePayload {
  name: string;
  source_system: string;
  entity: ImportEntity;
  mapping: Record<string, string>;
}

export interface ImportActionSummary {
  create_rows: number;
  update_rows: number;
  skip_rows: number;
  warning_rows: number;
  error_rows: number;
}

export interface StartImportPayload {
  import_job_id?: string;
  upload_id?: string;
  entity?: ImportEntity;
  mapping?: Record<string, string>;
  duplicate_action?: ImportDuplicateAction;
  is_dryrun?: boolean;
}

export interface ImportValidationIssue {
  row: number;
  column: string;
  message: string;
  severity: 'error' | 'warning';
}

export interface ImportValidationResult {
  total_rows: number;
  valid_rows: number;
  warning_rows: number;
  error_rows: number;
  errors: ImportValidationIssue[];
}

export interface ImportJobStatus {
  import_id: string;
  tenant_id: string;
  upload_id: string | null;
  entity: ImportEntity;
  file_name: string;
  duplicate_action: ImportDuplicateAction;
  mapping: Record<string, string>;
  status: 'pending' | 'processing' | 'awaiting_confirmation' | 'completed' | 'failed';
  progress: number;
  total_rows: number;
  processed_rows: number;
  success_rows: number;
  warning_rows: number;
  error_rows: number;
  parser_type: string | null;
  memory_peak_mb: number;
  error: string | null;
  created_by: string | null;
  is_dryrun: boolean;
  action_summary: ImportActionSummary | null;
  created_at?: string;
  started_at: string | null;
  completed_at: string | null;
  awaiting_confirmation_at: string | null;
  confirmed_at: string | null;
  validation_result: ImportValidationResult | null;
}

export interface DeleteImportJobResponse {
  success: boolean;
}

function mapDuplicateActionToImportMode(action?: ImportDuplicateAction): 'create' | 'upsert' {
  if (!action || action === 'upsert') return 'upsert';
  if (action === 'create') return 'create';
  throw new Error('Chế độ bỏ qua bản ghi trùng chưa được hỗ trợ bởi API import mới');
}

export const importService = {
  async upload(file: File): Promise<ImportUploadResponse> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post<ImportUploadResponse>('/excel/import/upload', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  async listTemplates(): Promise<ImportMappingTemplate[]> {
    const response = await api.get<ImportMappingTemplate[]>('/excel/templates');
    return response.data;
  },

  async createTemplate(payload: CreateImportTemplatePayload): Promise<ImportMappingTemplate> {
    const response = await api.post<ImportMappingTemplate>('/excel/templates', payload);
    return response.data;
  },

  async startImport(payload: StartImportPayload): Promise<ImportJobStatus> {
    if (payload.import_job_id && payload.is_dryrun === false) {
      const response = await api.post<ImportJobStatus>(`/excel/import-jobs/${payload.import_job_id}/commit`);
      return response.data;
    }

    if (!payload.upload_id || !payload.entity) {
      throw new Error('Thiếu upload_id hoặc entity để bắt đầu kiểm tra import');
    }

    const response = await api.post<ImportJobStatus>('/excel/import/validate', {
      upload_id: payload.upload_id,
      entity: payload.entity,
      mapping: payload.mapping,
      mode: mapDuplicateActionToImportMode(payload.duplicate_action),
    });
    return response.data;
  },

  async getStatus(id: string): Promise<ImportJobStatus> {
    const response = await api.get<ImportJobStatus>(`/excel/import-jobs/${id}/status`);
    return response.data;
  },

  async deleteJob(id: string): Promise<DeleteImportJobResponse> {
    const response = await api.delete<DeleteImportJobResponse>(`/excel/import-jobs/${id}`);
    return response.data;
  },

  async downloadErrorCsv(id: string): Promise<Blob> {
    const response = await api.get(`/excel/import-jobs/${id}/errors/csv`, {
      responseType: 'blob',
    });
    return response.data as Blob;
  },
};
